import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import styles from './GanttView.module.css';
import {
  toLocalDay,
  toLocalISODate,
  addDays,
  daysBetween,
  dateToColumn,
  getDateRange,
  buildHeaderCells,
  formatShortDate,
  isWeekend,
  COLUMN_WIDTHS,
} from './ganttUtils';

const SCALES = ['day', 'week', 'month'];
const EDGE_THRESHOLD_PX = 240;
const EXPAND_DAYS = 120;
const INITIAL_PAD_DAYS = 180;

const parseHexRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  if (hex.startsWith('rgb')) {
    const m = hex.match(/\d+/g);
    if (!m || m.length < 3) return null;
    return { r: Number(m[0]), g: Number(m[1]), b: Number(m[2]) };
  }
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (full.length !== 6) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const lighten = (hex, amount = 0.25) => {
  const rgb = parseHexRgb(hex);
  if (!rgb) return '#9ca3af';
  const blend = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${blend(rgb.r)}, ${blend(rgb.g)}, ${blend(rgb.b)})`;
};

const withAlpha = (hex, alpha) => {
  const rgb = parseHexRgb(hex);
  if (!rgb) return `rgba(107, 114, 128, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};


export default function GanttView({
  projects,
  canEditProjects,
  onProjectClick,
  onMilestoneClick,
  onProjectDateChange,
  onMilestoneDateChange,
}) {
  const [scale, setScale] = useState(() => localStorage.getItem('projectsGanttScale') || 'week');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [dragState, setDragState] = useState(null);
  const [dragDelta, setDragDelta] = useState({ days: 0, edge: null });
  const [pad, setPad] = useState({ left: INITIAL_PAD_DAYS, right: INITIAL_PAD_DAYS });

  const bodyRef = useRef(null);
  const didInitialScroll = useRef(false);
  const expandingRef = useRef(false);
  const suppressNextClickRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('projectsGanttScale', scale);
  }, [scale]);

  const columnWidth = COLUMN_WIDTHS[scale];

  const { start: dataStart, end: dataEnd } = useMemo(
    () => getDateRange(projects, 0),
    [projects]
  );

  const rangeStart = useMemo(() => addDays(dataStart, -pad.left), [dataStart, pad.left]);
  const rangeEnd = useMemo(() => addDays(dataEnd, pad.right), [dataEnd, pad.right]);

  const totalDays = useMemo(
    () => Math.max(1, daysBetween(rangeStart, rangeEnd) + 1),
    [rangeStart, rangeEnd]
  );

  const { primary, secondary } = useMemo(
    () => buildHeaderCells(rangeStart, totalDays, scale),
    [rangeStart, totalDays, scale]
  );

  const today = useMemo(() => toLocalDay(new Date()), []);
  const todayColumn = useMemo(
    () => dateToColumn(today, rangeStart),
    [today, rangeStart]
  );

  // Pre-compute weekend / week-start markers per column for body grid lines.
  const dayMarkers = useMemo(() => {
    const arr = [];
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(rangeStart, i);
      arr.push({
        weekend: isWeekend(d),
        weekStart: d.getDay() === 1, // Monday
      });
    }
    return arr;
  }, [rangeStart, totalDays]);

  const timelineWidth = totalDays * columnWidth;
  const gridTemplate = `repeat(${totalDays}, ${columnWidth}px)`;

  const toggleExpand = (projectId) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const allExpandableIds = useMemo(
    () => projects.filter((p) => p.milestones && p.milestones.length > 0).map((p) => p.project_id),
    [projects]
  );
  const allExpanded = allExpandableIds.length > 0 && allExpandableIds.every((id) => expandedProjects.has(id));
  const toggleExpandAll = () => {
    setExpandedProjects((prev) => (allExpanded ? new Set() : new Set(allExpandableIds)));
  };

  const scrollToToday = useCallback((behavior = 'smooth') => {
    if (!bodyRef.current) return;
    const target = (todayColumn - 1) * columnWidth - 200;
    bodyRef.current.scrollTo({ left: Math.max(0, target), behavior });
  }, [todayColumn, columnWidth]);

  // On scale change (after first render), jump to today so the view recenters.
  const prevScaleRef = useRef(scale);
  useEffect(() => {
    if (prevScaleRef.current !== scale) {
      scrollToToday('auto');
      prevScaleRef.current = scale;
    }
  }, [scale, scrollToToday]);

  // Auto-scroll on first render: always anchor the viewport on today.
  useEffect(() => {
    if (!bodyRef.current || didInitialScroll.current) return;
    const target = (todayColumn - 1) * columnWidth - 200;
    bodyRef.current.scrollLeft = Math.max(0, target);
    didInitialScroll.current = true;
  }, [todayColumn, columnWidth]);

  // When the left edge expands (rangeStart shifts earlier), keep the visible
  // content anchored by adjusting scrollLeft by the added width.
  const prevLeftPadRef = useRef(pad.left);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const diff = pad.left - prevLeftPadRef.current;
    if (diff > 0) {
      el.scrollLeft += diff * columnWidth;
    }
    prevLeftPadRef.current = pad.left;
    expandingRef.current = false;
  }, [pad.left, columnWidth]);

  // Infinite scroll: extend the range when the user nears either edge.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onScroll = () => {
      if (expandingRef.current) return;
      const leftGap = el.scrollLeft;
      const rightGap = el.scrollWidth - el.scrollLeft - el.clientWidth;
      if (leftGap < EDGE_THRESHOLD_PX) {
        expandingRef.current = true;
        setPad((p) => ({ ...p, left: p.left + EXPAND_DAYS }));
      } else if (rightGap < EDGE_THRESHOLD_PX) {
        expandingRef.current = true;
        setPad((p) => ({ ...p, right: p.right + EXPAND_DAYS }));
        // No scroll adjustment needed for right expansion.
        requestAnimationFrame(() => { expandingRef.current = false; });
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleMouseDown = (e, item, type, edge) => {
    if (!canEditProjects) return;
    if (type === 'project' && (!item.start_date || !item.end_date)) return;
    if (type === 'milestone' && (!item.start_date || !item.due_date)) return;
    e.stopPropagation();
    e.preventDefault();
    setDragState({
      type,
      edge,
      itemId: type === 'project' ? item.project_id : item.milestone_id,
      originX: e.clientX,
      originStart: item.start_date,
      originEnd: type === 'project' ? item.end_date : item.due_date,
    });
    setDragDelta({ days: 0, edge });
  };

  useEffect(() => {
    if (!dragState) return;

    const handleMove = (e) => {
      const dx = e.clientX - dragState.originX;
      const days = Math.round(dx / columnWidth);
      setDragDelta((prev) => (prev.days === days ? prev : { days, edge: dragState.edge }));
    };

    const handleUp = () => {
      const days = dragDelta.days;
      if (days !== 0) {
        suppressNextClickRef.current = true;
        const start = toLocalDay(dragState.originStart);
        const end = toLocalDay(dragState.originEnd);

        let newStart = start;
        let newEnd = end;
        if (dragState.edge === 'move') {
          newStart = addDays(start, days);
          newEnd = addDays(end, days);
        } else if (dragState.edge === 'left') {
          newStart = addDays(start, days);
          if (newStart >= end) newStart = addDays(end, -1);
        } else if (dragState.edge === 'right') {
          newEnd = addDays(end, days);
          if (newEnd <= start) newEnd = addDays(start, 1);
        }

        if (dragState.type === 'project' && onProjectDateChange) {
          onProjectDateChange(dragState.itemId, {
            start_date: toLocalISODate(newStart),
            end_date: toLocalISODate(newEnd),
          });
        } else if (dragState.type === 'milestone' && onMilestoneDateChange) {
          onMilestoneDateChange(dragState.itemId, {
            start_date: toLocalISODate(newStart),
            due_date: toLocalISODate(newEnd),
          });
        }
      }
      setDragState(null);
      setDragDelta({ days: 0, edge: null });
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragState, dragDelta.days, columnWidth, onProjectDateChange, onMilestoneDateChange]);

  const renderRowBackground = () => (
    <>
      {dayMarkers.map((m, i) => {
        const cls = [
          styles.gridCell,
          m.weekStart ? styles.gridCellWeekStart : '',
          m.weekend ? styles.gridCellWeekend : '',
        ]
          .filter(Boolean)
          .join(' ');
        return <div key={`g-${i}`} className={cls} style={{ gridColumn: `${i + 1} / ${i + 2}` }} />;
      })}
    </>
  );

  const renderBar = (item, type, color) => {
    const startRaw = item.start_date;
    const endRaw = type === 'project' ? item.end_date : item.due_date;
    if (!startRaw || !endRaw) {
      return null;
    }

    const itemId = type === 'project' ? item.project_id : item.milestone_id;
    const isDragging = dragState && dragState.itemId === itemId && dragState.type === type;
    const delta = isDragging ? dragDelta.days : 0;
    const edge = isDragging ? dragDelta.edge : null;

    let startDay = dateToColumn(startRaw, rangeStart);
    let endDay = dateToColumn(endRaw, rangeStart) + 1;
    if (edge === 'move') {
      startDay += delta;
      endDay += delta;
    } else if (edge === 'left') {
      startDay = Math.min(endDay - 1, startDay + delta);
    } else if (edge === 'right') {
      endDay = Math.max(startDay + 1, endDay + delta);
    }
    startDay = Math.max(1, startDay);
    endDay = Math.min(totalDays + 1, endDay);

    const barClasses = [
      styles.bar,
      type === 'milestone' ? styles.barMilestone : '',
      canEditProjects ? styles.barDraggable : '',
      isDragging ? styles.barDragging : '',
    ]
      .filter(Boolean)
      .join(' ');

    const label = type === 'project' ? item.project_name : item.milestone_name;

    return (
      <div
        className={barClasses}
        style={{
          gridColumn: `${startDay} / ${endDay}`,
          background: withAlpha(color, type === 'milestone' ? 0.2 : 0.28),
          borderLeft: `3px solid ${color}`,
        }}
        onMouseDown={(e) => handleMouseDown(e, item, type, 'move')}
        onClick={(e) => {
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            e.stopPropagation();
            return;
          }
          if (dragDelta.days !== 0) return;
          e.stopPropagation();
          if (type === 'project' && onProjectClick) onProjectClick(item);
          else if (type === 'milestone' && onMilestoneClick) onMilestoneClick(item);
        }}
        title={`${label}\n${formatShortDate(startRaw)} → ${formatShortDate(endRaw)}`}
      >
        {canEditProjects && (
          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleLeft}`}
            onMouseDown={(e) => handleMouseDown(e, item, type, 'left')}
          />
        )}
        <span className={styles.barLabel}>{label}</span>
        {canEditProjects && (
          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleRight}`}
            onMouseDown={(e) => handleMouseDown(e, item, type, 'right')}
          />
        )}
      </div>
    );
  };

  if (!projects || projects.length === 0) {
    return (
      <div className={styles.ganttRoot}>
        <div className={styles.emptyGantt}>No projects to display.</div>
      </div>
    );
  }

  // Build flat row list: project + (its milestones if expanded).
  const rows = [];
  projects.forEach((p) => {
    rows.push({ kind: 'project', project: p });
    if (expandedProjects.has(p.project_id) && p.milestones && p.milestones.length > 0) {
      [...p.milestones]
        .sort((a, b) => {
          const ad = a.start_date || a.due_date || '';
          const bd = b.start_date || b.due_date || '';
          return ad.localeCompare(bd);
        })
        .forEach((m) => {
          rows.push({ kind: 'milestone', project: p, milestone: m });
        });
    }
  });

  const renderProjectMeta = (p) => {
    if (!p.start_date && !p.end_date) return 'No dates';
    const s = formatShortDate(p.start_date);
    const e = formatShortDate(p.end_date);
    if (s && e) return `${s} → ${e}`;
    return s || e || '';
  };

  return (
    <div className={styles.ganttRoot}>
      <div className={styles.ganttToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.scaleGroup}>
            {SCALES.map((s) => (
              <button
                key={s}
                type="button"
                className={`${styles.scaleButton} ${s === scale ? styles.scaleButtonActive : ''}`}
                onClick={() => setScale(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {allExpandableIds.length > 0 && (
            <button type="button" className={styles.expandAllButton} onClick={toggleExpandAll}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          )}
          {canEditProjects && (
            <span className={styles.toolbarHint}>Drag bars to reschedule</span>
          )}
        </div>
        <div className={styles.toolbarRight}>
          <button type="button" className={styles.todayButton} onClick={() => scrollToToday()}>
            Today
          </button>
        </div>
      </div>

      <div className={styles.ganttBody} ref={bodyRef}>
        <div className={styles.leftColumn}>
          <div className={styles.leftHeader}>Project / Milestone</div>
          {rows.map((row, idx) => {
            if (row.kind === 'project') {
              const p = row.project;
              const expanded = expandedProjects.has(p.project_id);
              const hasMilestones = p.milestones && p.milestones.length > 0;
              return (
                <div
                  key={`p-${p.project_id}-${idx}`}
                  className={`${styles.leftRow} ${styles.leftRowProject}`}
                  onClick={() => onProjectClick && onProjectClick(p)}
                >
                  {hasMilestones ? (
                    <button
                      type="button"
                      className={styles.chevronButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(p.project_id);
                      }}
                      aria-label={expanded ? 'Collapse milestones' : 'Expand milestones'}
                    >
                      <svg
                        className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  ) : (
                    <span className={styles.chevronPlaceholder} />
                  )}
                  <span className={styles.colorDot} style={{ background: p.color || '#6b7280' }} />
                  <div className={styles.rowLabelWrap}>
                    <span className={styles.rowLabel}>
                      {p.project_name || <em style={{ color: '#9ca3af' }}>Untitled project</em>}
                    </span>
                    <span className={styles.rowMetaText}>{renderProjectMeta(p)}</span>
                  </div>
                  {hasMilestones && (
                    <span className={styles.milestoneCount}>{p.milestones.length}</span>
                  )}
                </div>
              );
            }
            const m = row.milestone;
            const meta =
              m.start_date && m.due_date
                ? `${formatShortDate(m.start_date)} → ${formatShortDate(m.due_date)}`
                : m.due_date
                  ? `Due ${formatShortDate(m.due_date)}`
                  : 'No dates';
            return (
              <div
                key={`m-${m.milestone_id}-${idx}`}
                className={`${styles.leftRow} ${styles.leftRowMilestone}`}
                onClick={() => onMilestoneClick && onMilestoneClick(m)}
                style={{ cursor: 'pointer' }}
              >
                <span className={styles.chevronPlaceholder} />
                <span
                  className={styles.colorDot}
                  style={{ background: lighten(row.project.color || '#6b7280', 0.15) }}
                />
                <div className={styles.rowLabelWrap}>
                  <span className={styles.rowLabel}>{m.milestone_name}</span>
                  <span className={styles.rowMetaText}>{meta}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.rightColumn} style={{ width: timelineWidth, minWidth: timelineWidth }}>
          <div className={styles.timelineHeader}>
            <div className={styles.headerPrimary} style={{ gridTemplateColumns: gridTemplate }}>
              {primary.map((cell, i) => (
                <div
                  key={`hp-${i}`}
                  className={styles.headerCell}
                  style={{ gridColumn: `span ${cell.span}` }}
                >
                  {cell.label}
                </div>
              ))}
            </div>
            <div className={styles.headerSecondary} style={{ gridTemplateColumns: gridTemplate }}>
              {secondary.map((cell, i) => (
                <div
                  key={`hs-${i}`}
                  className={`${styles.headerCellSecondary} ${cell.isWeekend ? styles.headerCellWeekend : ''}`}
                  style={{ gridColumn: `span ${cell.span}` }}
                >
                  {cell.label}
                </div>
              ))}
            </div>
          </div>

          {rows.map((row, idx) => {
            const color = row.project.color || '#6b7280';
            const isMilestone = row.kind === 'milestone';
            return (
              <div
                key={`tr-${idx}`}
                className={`${styles.timelineRow} ${isMilestone ? styles.timelineRowMilestone : ''}`}
                style={{ gridTemplateColumns: gridTemplate }}
              >
                {renderRowBackground()}
                {isMilestone
                  ? renderBar(row.milestone, 'milestone', lighten(color, 0.15))
                  : renderBar(row.project, 'project', color)}
              </div>
            );
          })}

          {todayColumn >= 1 && todayColumn <= totalDays && (
            <>
              <div
                className={styles.todayLine}
                style={{
                  left: (todayColumn - 1) * columnWidth + columnWidth / 2,
                  height: 64 + rows.length * 44,
                }}
              />
              <div
                className={styles.todayBadge}
                style={{ left: (todayColumn - 1) * columnWidth + columnWidth / 2 }}
              >
                Today
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
